import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { useMsal } from "@azure/msal-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";

import { hasValidAppToken, isAppToken } from "../utils/appToken";
import {
  getAccessTokenCookie,
  hasTabSessionEnded,
  isTokenStale,
  onExplicitLogoutElsewhere,
  onExternalSessionClearedElsewhere,
  setAccessTokenCookie,
  wasExplicitlyLoggedOut,
} from "../utils/cookies";
import {
  clearThisTabsMsalCache,
  isRedirectFallbackInFlight,
  shouldRedirectAfterSilentFailure,
  ssoSilentOnce,
  startRedirectFallback,
} from "../utils/msalTabSession";
import {
  isUserAuthenticated,
  logoutUser,
  refreshAppTokenIfNeeded,
  refreshTokenIfNeeded,
} from "../utils/tokenManager";

const AUTH_CALLBACK_PATH = "/auth-callback";
const LANDING_PATH = "/";

const REDIRECTING = "redirecting";

const currentRoute = () =>
  window.location.pathname + window.location.search + window.location.hash;

interface UseProtectedRouteReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Dropping the MSAL cache emits no event, so msal-react keeps the old account
// and Landing's Sign in would do nothing; a reload resets it.
const reloadToLanding = () => window.location.replace(LANDING_PATH);

/**
 * Hook for protecting routes and managing authentication state
 */
export const useProtectedRoute = (): UseProtectedRouteReturn => {
  const { accounts, instance } = useMsal();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  // Authenticated if MSAL has an account, or a valid token exists (handles new tab scenarios)
  const hasCredentials = accounts.length > 0 || isUserAuthenticated();

  /**
   * Result of the last completed `validateAuth`, or null before the first one.
   *
   * `hasCredentials` alone is not sufficient: MSAL can still hold an account
   * while a token refresh fails, so a known-invalid session would otherwise keep
   * rendering protected UI. That mattered less when the guard swapped its whole
   * subtree for a spinner on every navigation; now that it deliberately does not
   * (MDRS-233), the redirect is the only barrier left, so honour the validation
   * result too and withhold children while a session is known-invalid.
   */
  const [validated, setValidated] = useState<boolean | null>(null);
  // hasCredentials is read from the token store at render time, so a refresh
  // that leaves `validated` unchanged still needs a render to be seen.
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const isAuthenticated = hasCredentials && validated !== false;

  // Set when the cache was dropped under msal-react: leave by full reload.
  const msalReactStaleRef = useRef(false);

  const validateAuth = useCallback(async (): Promise<
    boolean | typeof REDIRECTING
  > => {
    // External users: a valid app token is a complete session — no MSAL needed.
    if (hasValidAppToken()) return true;

    // No in-memory app token yet — try the httpOnly refresh-token cookie
    // before concluding this isn't an external/OTP session at all (MDRS-556:
    // the OTP equivalent of ssoSilent below, since the access token is now
    // in-memory only and doesn't survive a fresh tab/reload on its own).
    // Not for an MSAL user (no refresh cookie — it would only 401, on every
    // navigation), and not after an explicit logout: the backend revoke there
    // is best-effort, so a cookie it failed to revoke must not sign the user
    // back in.
    if (
      accounts.length === 0 &&
      !wasExplicitlyLoggedOut() &&
      (await refreshAppTokenIfNeeded())
    ) {
      return true;
    }

    const validTokenInCookies = isUserAuthenticated();

    if (accounts.length === 0) {
      try {
        // msal-react's `accounts` can lag the cache.
        const cachedAccounts = instance.getAllAccounts();
        if (cachedAccounts.length > 0) {
          if (!hasTabSessionEnded()) {
            instance.setActiveAccount(cachedAccounts[0]);
            // A new tab has an account but no token yet; try for one before rendering.
            await refreshTokenIfNeeded();
            return true;
          }
          await clearThisTabsMsalCache(instance);
          // validTokenInCookies counted the account just dropped.
          return isUserAuthenticated();
        }

        // Microsoft's session outlives our logout; rejoining it would undo the
        // user's logout (MDRS-383).
        if (wasExplicitlyLoggedOut()) return validTokenInCookies;

        await ssoSilentOnce(instance);
        const refreshedAccounts = instance.getAllAccounts();
        // Another tab logged out while ssoSilent was pending.
        if (wasExplicitlyLoggedOut()) {
          await clearThisTabsMsalCache(instance);
          msalReactStaleRef.current = true;
          return false;
        }
        if (refreshedAccounts.length > 0) {
          instance.setActiveAccount(refreshedAccounts[0]);
          // A failed attempt still renders: the API's own 401 refresh covers it, not a Landing loop.
          await refreshTokenIfNeeded();
          return true;
        }
      } catch (error) {
        console.error("Silent authentication failed:", error);
        if (wasExplicitlyLoggedOut()) return false;
        const { pathname: path } = window.location;
        if (path !== LANDING_PATH && path !== AUTH_CALLBACK_PATH) {
          // A concurrent check already started it; its cooldown stamp would
          // otherwise send this one to Landing.
          if (isRedirectFallbackInFlight()) return REDIRECTING;
          if (shouldRedirectAfterSilentFailure(error)) {
            // Returns only if the page didn't leave.
            await startRedirectFallback(instance, currentRoute());
          }
        }
        // Fall through and attempt to use the existing token
      }

      return validTokenInCookies;
    }

    // A duplicated or reopened tab's account can predate a logout elsewhere.
    if (hasTabSessionEnded()) {
      await clearThisTabsMsalCache(instance);
      msalReactStaleRef.current = true;
      return false;
    }

    // Ensure active account is set
    if (!instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }

    return await refreshTokenIfNeeded();
  }, [accounts, instance]);

  const [isLoading, setIsLoading] = useState(true);

  // The auth check re-runs on every navigation (pathname is a dependency), but
  // only the FIRST run may gate rendering. Flipping `isLoading` back to true on
  // later runs made `ProtectedRoute` swap its whole subtree for a spinner, which
  // unmounted Layout → RecordingHost → Transcriber and killed any live
  // assessment recording mid-session, silently (MDRS-233). Re-validation now
  // happens in the background with the subtree left mounted; an invalid session
  // still redirects to the landing page below.
  const hasCompletedInitialCheckRef = useRef(false);

  // Read inside the listener below without re-subscribing on every
  // navigation (the listener is registered once, on mount).
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  // MDRS-383: an external (email-code) user's session lives outside MSAL's
  // cache, so MSAL's own cross-tab account-storage events don't cover it —
  // this reacts when another tab clears it, even though nothing about
  // `pathname` changed here to trigger the normal re-validation effect below.
  useEffect(() => {
    return onExternalSessionClearedElsewhere(() => {
      setValidated(false);
      if (
        pathnameRef.current !== LANDING_PATH &&
        pathnameRef.current !== AUTH_CALLBACK_PATH
      ) {
        navigate({ to: LANDING_PATH, replace: true });
      }
    });
  }, [navigate]);

  // The tab-scoped MSAL cache gets no event when another tab logs out.
  useEffect(() => {
    return onExplicitLogoutElsewhere(async () => {
      setValidated(false);
      await clearThisTabsMsalCache(instance);
      if (pathnameRef.current !== AUTH_CALLBACK_PATH) reloadToLanding();
    });
  }, [instance]);

  useEffect(() => {
    const checkAuth = async () => {
      if (!hasCompletedInitialCheckRef.current) setIsLoading(true);
      const isRedirectFlow = window.location.pathname === AUTH_CALLBACK_PATH;

      try {
        // Handle any pending redirect promise (user returning from login)
        const redirectResponse = await instance.handleRedirectPromise();
        if (redirectResponse?.account) {
          instance.setActiveAccount(redirectResponse.account);
          if (redirectResponse.accessToken) {
            setAccessTokenCookie(redirectResponse.accessToken);
          }
        }

        // Clear expired token if not in a redirect flow. App-issued (external)
        // tokens are exempt from the MSAL 15-min staleness rule — they're valid
        // for their own lifetime (renewed before it ends via the refresh
        // cookie), so only log out once that has expired.
        const token = getAccessTokenCookie();
        const hasAccounts = instance.getAllAccounts().length > 0;
        let appRefreshFailed = false;
        if (
          token &&
          isTokenStale() &&
          !hasValidAppToken() &&
          !isRedirectFlow &&
          !hasAccounts
        ) {
          if (isAppToken(token) && !wasExplicitlyLoggedOut()) {
            appRefreshFailed = !(await refreshAppTokenIfNeeded());
            if (appRefreshFailed) await logoutUser(false);
          } else {
            await logoutUser(false);
          }
        }

        const isValid = !appRefreshFailed && (await validateAuth());
        // Leaving for Microsoft: keep ProtectedRoute's spinner, don't navigate.
        if (isValid === REDIRECTING) return;
        setValidated(isValid);
        rerender();

        if (
          !isValid &&
          !isRedirectFlow &&
          pathname !== LANDING_PATH &&
          pathname !== AUTH_CALLBACK_PATH
        ) {
          const currentPath =
            pathname + window.location.search + window.location.hash;
          sessionStorage.setItem("targetRoute", currentPath);
          if (msalReactStaleRef.current) {
            reloadToLanding();
          } else {
            navigate({ to: LANDING_PATH, replace: true });
          }
        }
      } catch (error) {
        console.error("Authentication validation error:", error);
        setValidated(false);

        if (!isRedirectFlow) {
          await logoutUser(false);
          if (pathname !== LANDING_PATH && pathname !== AUTH_CALLBACK_PATH) {
            navigate({ to: LANDING_PATH, replace: true });
          }
        }
      } finally {
        hasCompletedInitialCheckRef.current = true;
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [validateAuth, pathname, navigate, instance]);

  return {
    isAuthenticated,
    isLoading,
  };
};
